import React, { useState } from 'react';
import { db } from '../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import { callClaude } from '../utils/claudeProxy';
import {
  Box, Typography, Button, Chip, CircularProgress, Alert,
  Accordion, AccordionSummary, AccordionDetails, LinearProgress,
  TextField, InputAdornment, Divider, Collapse
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import SearchIcon from '@mui/icons-material/Search';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

function questionKey(item) {
  if (item.number != null) return `q_${item.number}`;
  return 'q_' + item.questionText.trim().toLowerCase()
    .replace(/\s+/g, '_').slice(0, 60);
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function extractQAFromPDF(base64) {
  const data = await callClaude({
      model: 'claude-opus-5',
      max_tokens: 8000,
      messages: [{
        role: 'user',
        content: [
          {
            type: 'document',
            source: { type: 'base64', media_type: 'application/pdf', data: base64 }
          },
          {
            type: 'text',
            text: `Extrahera alla frågor och svar från detta upphandlingsdokument.
Returnera ENDAST ett JSON-objekt, ingen annan text:
{
  "qaItems": [
    {
      "number": 1,
      "questionDate": "2026-03-13",
      "questionText": "Fullständig frågetext här",
      "answerDate": "2026-03-16",
      "answerFrom": "Statens fastighetsverk",
      "answerText": "Fullständigt svar här"
    }
  ]
}
Ta med ALLA frågor och svar. Om ett svar saknas, sätt answerText till null.`
          }
        ]
      }]
    });
  const raw = data.content[0].text;
  const clean = raw.replace(/```json|```/g, '').trim();
  return JSON.parse(clean).qaItems || [];
}

export default function TenderQA({ tender, onUpdate, collapsed = false, onToggleCollapse }) {
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  const qaItems = tender.qaItems || [];
  const filtered = search.trim()
    ? qaItems.filter(q =>
        q.questionText?.toLowerCase().includes(search.toLowerCase()) ||
        q.answerText?.toLowerCase().includes(search.toLowerCase())
      )
    : qaItems;

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setAnalyzing(true);
    setError('');
    setProgress(20);
    try {
      const base64 = await fileToBase64(file);
      setProgress(50);
      const extracted = await extractQAFromPDF(base64);
      setProgress(80);

      // Merge: befintliga behålls, nya läggs till, befintliga uppdateras om svar ändrats
      const existingMap = {};
      qaItems.forEach(q => { existingMap[questionKey(q)] = q; });

      let added = 0, updated = 0;
      extracted.forEach(item => {
        const key = questionKey(item);
        const tagged = { ...item, id: key };
        if (!existingMap[key]) {
          existingMap[key] = tagged;
          added++;
        } else {
          // Uppdatera om svaret har ändrats
          if (existingMap[key].answerText !== item.answerText) {
            existingMap[key] = { ...existingMap[key], ...tagged };
            updated++;
          }
        }
      });

      const merged = Object.values(existingMap)
        .sort((a, b) => (a.number || 0) - (b.number || 0));

      await updateDoc(doc(db, 'tenders', tender.id), { qaItems: merged });
      onUpdate({ ...tender, qaItems: merged });

      setProgress(100);
      const msg = added > 0 && updated > 0
        ? `${added} nya frågor tillagda, ${updated} uppdaterade.`
        : added > 0
        ? `${added} nya frågor tillagda.`
        : updated > 0
        ? `${updated} svar uppdaterade.`
        : 'Inga nya frågor – listan är redan uppdaterad.';
      setSaved(msg);
      setTimeout(() => setSaved(''), 4000);
    } catch (err) {
      console.error(err);
      setError('Kunde inte läsa dokumentet. Försök igen.');
    } finally {
      setAnalyzing(false);
      setProgress(0);
      e.target.value = '';
    }
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', mb: 1 }}>
        <Box onClick={onToggleCollapse} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none', flex: 1, '&:hover': { opacity: 0.75 } }}>
          <ExpandMoreIcon sx={{ fontSize: 14, color: '#1565C0',
            transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
            transition: 'transform 0.2s', flexShrink: 0 }} />
          <QuestionAnswerIcon sx={{ fontSize: 16, color: '#1565C0' }} />
          <Typography variant="caption" fontWeight="bold"
            sx={{ color: '#1565C0' }}>
            FRÅGOR OCH SVAR
          </Typography>
          {qaItems.length > 0 && (
            <Chip label={`${qaItems.length} st`} size="small"
              sx={{ fontSize: 10, height: 18, bgcolor: 'rgba(21,101,192,0.15)', color: '#1565C0' }} />
          )}
        </Box>
        <Button size="small" component="label" variant="outlined"
          startIcon={analyzing
            ? <CircularProgress size={12} />
            : <AutoFixHighIcon sx={{ fontSize: 14 }} />}
          disabled={analyzing}
          sx={{ fontSize: '0.65rem', py: 0.2, px: 1,
            borderColor: '#1565C0', color: '#1565C0',
            '&:hover': { borderColor: '#0d47a1', bgcolor: 'rgba(21,101,192,0.08)' } }}>
          {analyzing ? 'Analyserar...' : qaItems.length > 0 ? 'Ladda upp uppdatering' : 'Ladda upp Q&A-PDF'}
          <input type="file" hidden accept=".pdf" onChange={handleUpload} />
        </Button>
      </Box>

      <Collapse in={!collapsed}>
      {analyzing && (
        <LinearProgress variant="determinate" value={progress}
          sx={{ mb: 1, '& .MuiLinearProgress-bar': { bgcolor: '#1565C0' } }} />
      )}
      {error && <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 1, py: 0.5 }}>{saved}</Alert>}

      {qaItems.length === 0 && !analyzing && (
        <Box sx={{ py: 1.5, px: 1, bgcolor: 'action.hover',
          borderRadius: 1, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Ladda upp PDF med frågor och svar från upphandlingsportalen
          </Typography>
        </Box>
      )}

      {qaItems.length > 0 && (
        <>
          <TextField size="small" fullWidth
            placeholder="Sök i frågor och svar..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ mb: 1,
              '& .MuiInputBase-input': { py: 0.6, fontSize: '0.8rem' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16 }} />
                </InputAdornment>
              )
            }}
          />
          <Typography variant="caption" color="text.secondary"
            sx={{ display: 'block', mb: 0.5 }}>
            {filtered.length} av {qaItems.length} frågor
          </Typography>
          {filtered.map((q, i) => (
            <Accordion key={q.id || i} disableGutters elevation={0}
              expanded={expanded === (q.id || i)}
              onChange={(_, open) => setExpanded(open ? (q.id || i) : null)}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '6px !important',
                mb: 0.5,
                '&:before': { display: 'none' },
                overflow: 'hidden'
              }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                sx={{ minHeight: 0, py: 0.5, px: 1.5,
                  '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Chip label={`F${q.number}`} size="small"
                    sx={{ fontSize: 10, height: 18, bgcolor: '#1565C0',
                      color: 'white', flexShrink: 0 }} />
                  <Typography variant="caption" sx={{
                    flex: 1, overflow: 'hidden',
                    display: '-webkit-box', WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}>
                    {q.questionText}
                  </Typography>
                  {q.answerText ? (
                    <Chip label="Besvarad" size="small"
                      sx={{ fontSize: 9, height: 16, bgcolor: 'rgba(46,125,50,0.15)',
                        color: '#2e7d32', flexShrink: 0 }} />
                  ) : (
                    <Chip label="Inväntar svar" size="small"
                      sx={{ fontSize: 9, height: 16, bgcolor: 'rgba(245,127,23,0.15)',
                        color: '#f57f17', flexShrink: 0 }} />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
                <Box sx={{ bgcolor: 'rgba(21,101,192,0.08)', p: 1.5, borderRadius: 1, mb: 1 }}>
                  <Typography variant="caption" fontWeight="bold"
                    sx={{ color: '#1565C0' }}>
                    FRÅGA {q.questionDate && `· ${q.questionDate}`}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {q.questionText}
                  </Typography>
                </Box>
                {q.answerText ? (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ bgcolor: 'rgba(46,125,50,0.08)', p: 1.5, borderRadius: 1 }}>
                      <Typography variant="caption" fontWeight="bold"
                        sx={{ color: '#2e7d32' }}>
                        SVAR{q.answerFrom && ` från ${q.answerFrom}`}
                        {q.answerDate && ` · ${q.answerDate}`}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                        {q.answerText}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary"
                    sx={{ fontStyle: 'italic' }}>
                    Svar inväntas...
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}
      </Collapse>
    </Box>
  );
}
